/* Pro application stepper: one short question per step, tap-to-advance tiles, inline validation,
   draft kept in sessionStorage. Without JS every step shows at once and the form still submits. */
(() => {
  if (customElements.get('apply-stepper')) return;
  const KEY = 'mg-pro-apply';
  const store = {
    get() { try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (_) { return {}; } },
    set(v) { try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (_) { /* private mode */ } },
    clear() { try { sessionStorage.removeItem(KEY); } catch (_) { /* ignore */ } },
  };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define('apply-stepper', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      const done = this.querySelector('[data-ap-done]');
      if (done) {
        store.clear();
        requestAnimationFrame(() => {
          done.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
          done.focus({ preventScroll: true });
        });
        return;
      }
      if (!this.form) return;
      this.steps = [...this.querySelectorAll('[data-ap-step]')];
      this.top = this.querySelector('[data-ap-top]');
      this.next = this.querySelector('[data-ap-next]');
      this.submit = this.querySelector('[data-ap-submit]');
      this.progress = this.querySelector('[data-ap-progress]');
      this.count = this.querySelector('[data-ap-count]');
      this.classList.add('is-ready');
      this.top.hidden = false;
      this.progress.setAttribute('aria-valuemax', String(this.steps.length));

      this.restore();
      this.setupUpload();
      // Phone: required, at least 10 digits; spaces, dashes, dots, brackets and a leading + are fine.
      this.form.querySelectorAll('[data-ap-phone]').forEach((el) => {
        this.checkPhone(el);
        el.addEventListener('input', () => this.checkPhone(el));
      });
      const email = new URLSearchParams(location.search).get('email');
      const emailInput = this.form.querySelector('input[type="email"]');
      if (email && emailInput && !emailInput.value) emailInput.value = email;

      // Start on the first step with a missing answer (useful after a reload or a server error).
      let start = 0;
      if (this.form.querySelector('.form-msg--error')) start = 1;
      else {
        const firstIncomplete = this.steps.findIndex((st) => !this.stepValid(st, false));
        start = firstIncomplete === -1 ? this.steps.length - 1 : firstIncomplete;
      }
      this.go(start, false);

      const toggle = this.querySelector('[data-ap-pass-toggle]');
      if (toggle) toggle.addEventListener('click', () => {
        const input = document.getElementById(toggle.getAttribute('aria-controls'));
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        toggle.textContent = show ? 'Hide' : 'Show';
        toggle.setAttribute('aria-pressed', String(show));
      });
      this.next.addEventListener('click', () => this.advance());
      this.querySelector('[data-ap-back]').addEventListener('click', () => this.go(this.index - 1));
      // Tiles auto-advance on tap/click only; keyboard users move with arrows and press Enter or Continue.
      this.form.addEventListener('pointerdown', (e) => { if (e.target.closest('.ap__tile')) this.viaPointer = true; });
      this.form.addEventListener('change', (e) => {
        this.save();
        const t = e.target;
        if (t.matches('.ap__tile input')) {
          this.clearError(t);
          const pointer = this.viaPointer;
          this.viaPointer = false;
          const from = this.steps.indexOf(t.closest('[data-ap-step]'));
          // Only auto-advance if the pro hasn't already moved on (e.g. tapped Continue right after the tile).
          if (pointer && this.index === from) setTimeout(() => { if (this.index === from) this.advance(); }, reduceMotion ? 0 : 220);
        }
      });
      this.form.addEventListener('input', (e) => { this.save(); if (e.target.getAttribute('aria-invalid') === 'true') this.validateField(e.target); });
      this.form.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA' || e.target.type === 'submit' || e.target.type === 'button') return;
        if (this.index < this.steps.length - 1) { e.preventDefault(); this.advance(); }
      });
      this.form.addEventListener('submit', (e) => {
        if (this.uploading) {
          e.preventDefault();
          this.submit.disabled = true;
          this.submit.textContent = 'Finishing your upload…';
          this.uploading.finally(() => { this.submit.disabled = false; this.form.requestSubmit ? this.form.requestSubmit(this.submit) : this.form.submit(); });
          return;
        }
        const bad = this.steps.findIndex((st) => !this.stepValid(st, false));
        if (bad !== -1) {
          e.preventDefault();
          this.go(bad);
          this.stepValid(this.steps[bad], true);
          return;
        }
        const first = this.form.querySelector('[data-ap-first]');
        const last = this.form.querySelector('[data-ap-last]');
        const full = this.form.querySelector('[data-ap-fullname]');
        if (full) full.value = [first && first.value.trim(), last && last.value.trim()].filter(Boolean).join(' ');
        this.submit.disabled = true;
        this.submit.classList.add('is-loading');
        this.submit.textContent = this.form.querySelector('[name="customer[password]"]') ? 'Creating your account…' : 'Sending…';
      });
    }

    checkPhone(el) {
      const v = el.value.trim();
      const digits = v.replace(/\D/g, '');
      const bad = v && (!/^\+?[\d\s().-]+$/.test(v) || digits.length < 10 || digits.length > 15);
      el.setCustomValidity(bad ? 'Enter a phone number with at least 10 digits, like 305 555 0123 or +44 20 7946 0958.' : '');
    }

    fields(step) { return [...step.querySelectorAll('input, select, textarea')].filter((el) => el.type !== 'hidden'); }

    message(el) {
      if (el.validity.valueMissing) {
        if (el.type === 'email') return 'Enter your email.';
        if (el.type === 'password' || el.name === 'customer[password]') return 'Create a password (at least 5 characters).';
        if (el.type === 'tel') return 'Enter your phone number.';
        const label = el.id ? this.querySelector(`label[for="${el.id}"]`) : null;
        return label ? `Enter your ${label.childNodes[0].textContent.trim().toLowerCase()}.` : 'This field is required.';
      }
      if (el.validity.tooShort) return `Use at least ${el.minLength} characters.`;
      if ((el.validity.typeMismatch || el.validity.patternMismatch) && el.type === 'email') return 'Enter a valid email, like name@salon.com.';
      if (el.type === 'tel' && (el.validity.customError || el.validity.patternMismatch)) return 'Enter a phone number with at least 10 digits, like 305 555 0123 or +44 20 7946 0958.';
      return el.validationMessage;
    }

    errorEl(el) {
      if (el.type === 'radio') return this.querySelector(`[data-ap-error-for="${el.name}"]`);
      return el.closest('.field') ? el.closest('.field').querySelector('.ap__error') : null;
    }

    clearError(el) {
      const box = this.errorEl(el);
      if (box) box.hidden = true;
      el.removeAttribute('aria-invalid');
    }

    validateField(el) {
      const box = this.errorEl(el);
      if (el.type === 'radio') {
        const group = [...this.form.querySelectorAll(`input[name="${CSS.escape(el.name)}"]`)];
        const ok = !group.some((r) => r.required) || group.some((r) => r.checked);
        if (box) box.hidden = ok;
        return ok;
      }
      if (el.type === 'email') el.value = el.value.trim();
      const ok = el.checkValidity();
      el.toggleAttribute('aria-invalid', !ok);
      if (!ok) el.setAttribute('aria-invalid', 'true');
      if (box) { box.textContent = ok ? '' : this.message(el); box.hidden = ok; }
      return ok;
    }

    stepValid(step, show) {
      let firstBad = null;
      const seen = new Set();
      for (const el of this.fields(step)) {
        if (el.type === 'radio') { if (seen.has(el.name)) continue; seen.add(el.name); }
        const ok = show ? this.validateField(el) : (el.type === 'radio'
          ? !el.required || !!this.form.querySelector(`input[name="${CSS.escape(el.name)}"]:checked`)
          : el.checkValidity());
        if (!ok && !firstBad) firstBad = el;
      }
      if (show && firstBad) firstBad.focus();
      return !firstBad;
    }

    advance() {
      if (!this.stepValid(this.steps[this.index], true)) return;
      if (this.index < this.steps.length - 1) this.go(this.index + 1);
    }

    go(i, focus = true) {
      i = Math.max(0, Math.min(i, this.steps.length - 1));
      const dir = i >= (this.index || 0) ? 'fwd' : 'back';
      this.index = i;
      this.steps.forEach((st, n) => {
        const on = n === i;
        st.hidden = !on;
        st.classList.toggle('is-active', on);
        st.dataset.dir = dir;
      });
      const last = i === this.steps.length - 1;
      this.next.hidden = last;
      this.submit.hidden = !last;
      this.top.classList.toggle('is-first', i === 0);
      [...this.progress.children].forEach((bar, n) => bar.classList.toggle('is-on', n <= i));
      this.progress.setAttribute('aria-valuenow', String(i + 1));
      this.count.textContent = `${i + 1} of ${this.steps.length}`;
      if (!focus) return;
      const step = this.steps[i];
      const target = this.fields(step).find((el) => el.type !== 'radio' && !el.value) || step.querySelector('.ap__q');
      const rect = this.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
        const offset = (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h'), 10) || 64) + 16;
        window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      if (target) target.focus({ preventScroll: true });
    }

    setupUpload() {
      const box = this.querySelector('[data-ap-upload]');
      if (!box) return;
      this.up = {
        box,
        input: box.querySelector('[data-ap-file]'),
        drop: box.querySelector('[data-ap-drop]'),
        row: box.querySelector('[data-ap-file-row]'),
        name: box.querySelector('[data-ap-file-name]'),
        status: box.querySelector('[data-ap-file-status]'),
        icon: box.querySelector('[data-ap-file-icon]'),
        url: box.querySelector('[data-ap-file-url]'),
        label: box.querySelector('[data-ap-file-label]'),
        variant: box.dataset.variant,
      };
      const u = this.up;
      if (u.url.value) this.showFile(u.label.value || 'Your file', 'done');
      u.input.addEventListener('change', () => { if (u.input.files && u.input.files[0]) this.uploadFile(u.input.files[0]); });
      box.querySelector('[data-ap-file-remove]').addEventListener('click', () => this.clearFile());
      ['dragenter', 'dragover'].forEach((t) => u.drop.addEventListener(t, (e) => { e.preventDefault(); u.drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((t) => u.drop.addEventListener(t, (e) => { e.preventDefault(); u.drop.classList.remove('is-over'); }));
      u.drop.addEventListener('drop', (e) => { const f = e.dataTransfer && e.dataTransfer.files[0]; if (f) this.uploadFile(f); });
    }

    showFile(name, state, message) {
      const u = this.up;
      u.drop.hidden = state === 'done' || state === 'busy';
      u.row.hidden = false;
      u.row.dataset.state = state;
      u.name.textContent = name;
      u.status.textContent = message || (state === 'busy' ? 'Uploading…' : state === 'done' ? 'Uploaded' : '');
      u.icon.textContent = state === 'error' ? '!' : state === 'busy' ? '' : '✓';
    }

    clearFile() {
      const u = this.up;
      u.url.value = '';
      u.label.value = '';
      u.input.value = '';
      u.row.hidden = true;
      u.drop.hidden = false;
      this.save();
    }

    async uploadFile(file) {
      const u = this.up;
      const okType = /^image\//.test(file.type) || file.type === 'application/pdf' || /\.(pdf|jpe?g|png|heic|heif|webp)$/i.test(file.name);
      if (!okType) { this.showFile(file.name, 'error', 'Please choose a photo or a PDF.'); u.drop.hidden = false; return; }
      if (file.size > 10 * 1024 * 1024) { this.showFile(file.name, 'error', 'That file is over 10 MB. Try a smaller photo or PDF.'); u.drop.hidden = false; return; }
      this.showFile(file.name, 'busy');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const run = (async () => {
        const fd = new FormData();
        fd.append('id', u.variant);
        fd.append('quantity', '1');
        fd.append('properties[_Pro license upload]', file, file.name);
        const res = await fetch(root + 'cart/add.js', { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } });
        const item = await res.json();
        const url = item && item.properties && item.properties['_Pro license upload'];
        // The cart line only carried the file; take it straight back out.
        if (item && item.key) {
          await fetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) }).catch(() => {});
        }
        if (!res.ok || !url) throw new Error((item && (item.description || item.message)) || 'Upload failed');
        return url;
      })();
      this.uploading = run;
      try {
        const url = await run;
        u.url.value = url;
        u.label.value = file.name;
        this.showFile(file.name, 'done');
        this.save();
      } catch (err) {
        u.url.value = '';
        this.showFile(file.name, 'error', "Couldn't upload that file. You can still continue and email it to us later.");
        u.drop.hidden = false;
      } finally {
        this.uploading = null;
        u.input.value = '';
      }
    }

    save() {
      const data = {};
      for (const el of this.form.elements) {
        if (el.hasAttribute && el.hasAttribute('data-ap-persist')) { if (el.value) data['@' + (el.name || '')] = el.value; continue; }
        if (!el.name || el.type === 'hidden' || el.type === 'password' || el.type === 'submit' || el.type === 'file') continue;
        if (el.type === 'radio') { if (el.checked) data[el.name] = el.value; } else data[el.name] = el.value;
      }
      store.set(data);
    }

    restore() {
      const data = store.get();
      for (const [key, value] of Object.entries(data)) {
        if (key.startsWith('@')) {
          const el = this.form.querySelector(`[data-ap-persist][name="${CSS.escape(key.slice(1))}"]`);
          if (el && !el.value) el.value = value;
          continue;
        }
        const name = key;
        const els = [...this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`)];
        els.forEach((el) => {
          if (el.type === 'radio') el.checked = el.value === value;
          else if (!el.value) el.value = value;
        });
      }
    }
  });
})();
